<section class="tasks-screen" aria-label="Tasks screen">
  <div class="tasks-workspace">
    <aside class="tasks-sidebar" aria-label="Task workspace context">
      <div class="tasks-sidebar__header">
        <div>
          <p class="tasks-sidebar__eyebrow">Tasks</p>
          <h2>Views</h2>
        </div>
        <div class="tasks-sidebar__header-actions">
          <button type="button" mat-icon-button class="tasks-sidebar__icon-button" (click)="toggleSearch()" aria-label="Search tasks" matTooltip="Search tasks" matTooltipPosition="right">
            <mat-icon aria-hidden="true">{{ showSearch() ? 'close' : 'search' }}</mat-icon>
          </button>
        </div>
      </div>

      @if (showSearch()) {
        <mat-form-field appearance="outline" class="tasks-sidebar__search">
          <mat-label>Search tasks</mat-label>
          <input matInput [value]="searchTerm()" (input)="searchTerm.set($any($event.target).value)" placeholder="Search title, subtask owner, or tags" />
        </mat-form-field>
      }

      @if (favoriteSavedFilters().length) {
        <section class="tasks-sidebar__saved" aria-label="Pinned task views">
          <div class="tasks-sidebar__saved-head">
            <span>Pinned views</span>
          </div>
          <div class="tasks-sidebar__saved-list">
            @for (filter of favoriteSavedFilters(); track filter.id) {
              <button
                type="button"
                class="tasks-sidebar__saved-item"
                [class.tasks-sidebar__saved-item--active]="isSavedFilterActive(filter)"
                (click)="applySavedFilter(filter)"
              >
                <span>{{ filter.name }}</span>
                @if (isDefaultSavedFilter(filter)) {
                  <small>Default</small>
                }
              </button>
            }
          </div>
        </section>
      }

      @if (selectedProjectId() && unassignedWorkload().total > 0) {
        <section class="tasks-sidebar__summary" aria-label="Unowned subtask work summary">
          <div class="tasks-sidebar__summary-head">
            <span>No subtask owner</span>
            <strong>{{ unassignedWorkload().total }}</strong>
          </div>
          <div class="tasks-sidebar__summary-metrics">
            <span>{{ unassignedWorkload().dueToday }} due today</span>
            <span [class.tasks-sidebar__summary-metrics--overdue]="unassignedWorkload().overdue > 0">{{ unassignedWorkload().overdue }} overdue</span>
          </div>
        </section>
      }

      @if (selectedProjectId() && assigneeWorkload().length) {
        <section class="tasks-sidebar__workload" aria-label="Subtask owner workload">
          <div class="tasks-sidebar__workload-head">
            <span>Subtask owner workload</span>
            <strong>{{ assigneeWorkload().length }}</strong>
          </div>
          <div class="tasks-sidebar__workload-list">
            @for (assignee of assigneeWorkload(); track assignee.id) {
              <div class="tasks-sidebar__workload-item">
                <span class="tasks-sidebar__workload-name">
                  <span class="task-avatar task-avatar--small">{{ getUserInitials(assignee.id || null) }}</span>
                  {{ assignee.name }}
                </span>
                <div class="tasks-sidebar__workload-metrics">
                  <span>{{ assignee.total }} open</span>
                  <span>{{ assignee.dueToday }} today</span>
                  <span [class.tasks-sidebar__workload-metrics--overdue]="assignee.overdue > 0">{{ assignee.overdue }} overdue</span>
                </div>
              </div>
            }
          </div>
        </section>
      }

      @if (!projects().length) {
        <mat-card class="tasks-empty-card tasks-empty-card--sidebar">
          <mat-icon aria-hidden="true">assignment</mat-icon>
          <h3>No projects yet</h3>
          <p>Create your first project to start organizing work in {{ brand.name }}.</p>
          @if (canManageProject()) {
            <button type="button" mat-flat-button color="primary" (click)="openProjectDialog()">Create project</button>
          }
        </mat-card>
      }
    </aside>

    <section class="tasks-main">
      <header class="tasks-header-shell">
        <div class="tasks-header">
          <div class="tasks-header__content">
            <div class="tasks-header__title-row">
              <mat-menu #projectSwitcherMenu="matMenu" class="tasks-filter-menu">
                <div class="tasks-filter-menu__panel tasks-project-switcher-menu" (click)="$event.stopPropagation()">
                  <div class="tasks-filter-menu__header">
                    <span class="tasks-filter-menu__title">Projects</span>
                    @if (canCreateProject()) {
                      <button type="button" class="tasks-filter-menu__save" (click)="closeMenu(projectSwitcherTrigger); openProjectDialog()">New project</button>
                    }
                  </div>
                  <div class="tasks-project-switcher-menu__list">
                    @for (project of projects(); track project.id) {
                      <button
                        type="button"
                        class="tasks-project-switcher-menu__item"
                        [class.tasks-project-switcher-menu__item--active]="selectedProjectId() === project.id"
                        (click)="closeMenu(projectSwitcherTrigger); onProjectChange(project.id)"
                      >
                        <span>{{ formatProjectTitle(project.name) }}</span>
                        <small>{{ selectedProjectId() === project.id ? projectTasks().length : '' }}</small>
                      </button>
                    }
                  </div>
                </div>
              </mat-menu>
              <button
                #projectSwitcherTrigger="matMenuTrigger"
                type="button"
                class="tasks-header__project-switcher"
                [matMenuTriggerFor]="projectSwitcherMenu"
                aria-label="Switch project"
              >
                <span>{{ formatProjectTitle(getSelectedProject()?.name) }}</span>
                <mat-icon aria-hidden="true">expand_more</mat-icon>
              </button>
              @if (selectedProjectId() && canManageProject()) {
                <mat-menu #activeProjectMenu="matMenu">
                  @if (getSelectedProject(); as activeProject) {
                    <button type="button" mat-menu-item (click)="closeMenu(activeProjectMenuTrigger); openProjectDialog(activeProject)">
                      <mat-icon aria-hidden="true">edit</mat-icon>
                      <span>Edit project</span>
                    </button>
                    <button type="button" mat-menu-item (click)="closeMenu(activeProjectMenuTrigger); archiveProject(activeProject)">
                      <mat-icon aria-hidden="true">archive</mat-icon>
                      <span>Archive project</span>
                    </button>
                  }
                </mat-menu>
                <button
                  #activeProjectMenuTrigger="matMenuTrigger"
                  type="button"
                  mat-icon-button
                  class="tasks-header__project-menu"
                  [matMenuTriggerFor]="activeProjectMenu"
                  aria-label="Project actions"
                  matTooltip="Project actions"
                  matTooltipPosition="below"
                >
                  <mat-icon aria-hidden="true">more_horiz</mat-icon>
                </button>
              }
              @if (selectedProjectId()) {
                <span class="tasks-header__badge">{{ projectTasks().length }} tasks</span>
              }
              @if (selectedProjectId() && selectedView() !== 'reports') {
                <mat-menu #sprintSwitcherMenu="matMenu" class="tasks-filter-menu">
                  <div class="tasks-filter-menu__panel tasks-project-switcher-menu" (click)="$event.stopPropagation()">
                    <div class="tasks-filter-menu__header">
                      <span class="tasks-filter-menu__title">Sprint filter</span>
                    </div>
                    <div class="tasks-project-switcher-menu__list">
                      <button
                        type="button"
                        class="tasks-project-switcher-menu__item"
                        [class.tasks-project-switcher-menu__item--active]="isSprintFilterSelected('__all__')"
                        (click)="closeMenu(sprintSwitcherTrigger); onSprintFilterChange('__all__')"
                      >
                        <span>All sprints</span>
                      </button>
                      @if (activeSprint()) {
                        <button
                          type="button"
                          class="tasks-project-switcher-menu__item"
                          [class.tasks-project-switcher-menu__item--active]="isSprintFilterSelected('__active__')"
                          (click)="closeMenu(sprintSwitcherTrigger); onSprintFilterChange('__active__')"
                        >
                          <span>Active: {{ activeSprint()!.name }}</span>
                        </button>
                      }
                      <button
                        type="button"
                        class="tasks-project-switcher-menu__item"
                        [class.tasks-project-switcher-menu__item--active]="isSprintFilterSelected('__none__')"
                        (click)="closeMenu(sprintSwitcherTrigger); onSprintFilterChange('__none__')"
                      >
                        <span>No sprint</span>
                      </button>
                      @for (sprint of sprints(); track sprint.id) {
                        <button
                          type="button"
                          class="tasks-project-switcher-menu__item"
                          [class.tasks-project-switcher-menu__item--active]="isSprintFilterSelected(sprint.id)"
                          (click)="closeMenu(sprintSwitcherTrigger); onSprintFilterChange(sprint.id)"
                        >
                          <span>{{ sprint.name }}</span>
                          @if (sprint.is_active) {
                            <small>Active</small>
                          }
                        </button>
                      }
                    </div>
                  </div>
                </mat-menu>
                <button
                  #sprintSwitcherTrigger="matMenuTrigger"
                  type="button"
                  class="tasks-header__project-switcher tasks-header__project-switcher--sprint"
                  [matMenuTriggerFor]="sprintSwitcherMenu"
                  aria-label="Switch sprint filter"
                >
                  <span>{{ selectedSprintFilterLabel() }}</span>
                  <mat-icon aria-hidden="true">expand_more</mat-icon>
                </button>
              }
              @if (selectedProjectId()) {
                <span class="tasks-header__badge tasks-header__badge--scope">{{ activeScopeLabel() }}</span>
              }
              @if (selectedTagFilters().length || selectedAssigneeFilters().length) {
                <div class="tasks-header__active-filters" aria-label="Active task filters">
                  @for (tag of activeTagFilterBadges(); track tag) {
                    <span class="tasks-header__badge tasks-header__badge--filter">Tag: {{ tag }}</span>
                  }
                  @if (activeTagFilterOverflowCount() > 0) {
                    <span class="tasks-header__badge tasks-header__badge--filter">+{{ activeTagFilterOverflowCount() }} tags</span>
                  }
                  @for (assignee of activeAssigneeFilterBadges(); track assignee.id) {
                    <span class="tasks-header__badge tasks-header__badge--filter">Assignee: {{ assignee.name }}</span>
                  }
                  @if (activeAssigneeFilterOverflowCount() > 0) {
                    <span class="tasks-header__badge tasks-header__badge--filter">+{{ activeAssigneeFilterOverflowCount() }} assignees</span>
                  }
                </div>
              }
              @if (defaultSavedFilter()) {
                <span class="tasks-header__badge tasks-header__badge--default">Default: {{ defaultSavedFilter()!.name }}</span>
              }
              @if (!selectedProjectId()) {
                <p>Pick a project from the switcher to view its board or list.</p>
              }
            </div>
            @if (favoriteSavedFilters().length) {
              <div class="tasks-header__saved-row">
                @for (filter of favoriteSavedFilters().slice(0, 3); track filter.id) {
                  <button
                    type="button"
                    class="tasks-header__saved-chip"
                    [class.tasks-header__saved-chip--active]="isSavedFilterActive(filter)"
                    (click)="applySavedFilter(filter)"
                  >
                    {{ filter.name }}
                  </button>
                }
              </div>
            }
          </div>

          @if (selectedProjectId()) {
            <div class="tasks-header__actions">
              <button
                type="button"
                mat-icon-button
                color="primary"
                [disabled]="!selectedProjectId()"
                (click)="openTaskDialog()"
                aria-label="New task"
                matTooltip="New task"
                matTooltipPosition="below"
              >
                <mat-icon aria-hidden="true">add_task</mat-icon>
              </button>
              <button
                #tasksFilterTrigger="matMenuTrigger"
                type="button"
                mat-icon-button
                [class.tasks-header__filter-button--active]="hasActiveFilters()"
                [matMenuTriggerFor]="tasksFilterMenu"
                aria-label="Filter tasks"
                matTooltip="Filter tasks"
                matTooltipPosition="below"
              >
                <mat-icon aria-hidden="true">filter_list</mat-icon>
              </button>
              @if (canManageProject() && selectedView() === 'board') {
                <button
                  type="button"
                  mat-icon-button
                  (click)="openColumnDialog()"
                  aria-label="Add column"
                  matTooltip="Add column"
                  matTooltipPosition="below"
                >
                  <mat-icon aria-hidden="true">view_column</mat-icon>
                </button>
              }
              <mat-button-toggle-group class="tasks-view-toggle" [value]="selectedView()" (valueChange)="onViewChange($event)">
                <mat-button-toggle value="board">Board</mat-button-toggle>
                <mat-button-toggle value="list">List</mat-button-toggle>
                <mat-button-toggle value="reports">Reports</mat-button-toggle>
              </mat-button-toggle-group>
            </div>
            <mat-menu #tasksFilterMenu="matMenu" class="tasks-filter-menu">
              <div class="tasks-filter-menu__panel" (click)="$event.stopPropagation()">
                <div class="tasks-filter-menu__header">
                  <span class="tasks-filter-menu__title">Filters</span>
                  <button
                    type="button"
                    class="tasks-filter-menu__clear"
                    [disabled]="!hasActiveFilters()"
                    (click)="clearFilters()"
                  >
                    Clear
                  </button>
                </div>
                <div class="tasks-filter-menu__section">
                  <span class="tasks-filter-menu__label">Scope</span>
                  <div class="tasks-filter-menu__options">
                    @for (option of scopeOptions; track option.value) {
                      <button
                        type="button"
                        class="tasks-filter-menu__chip"
                        [class.tasks-filter-menu__chip--active]="selectedScope() === option.value"
                        (click)="onScopeChange(option.value)"
                      >
                        {{ option.label }}
                      </button>
                    }
                  </div>
                </div>
                <div class="tasks-filter-menu__section">
                  <span class="tasks-filter-menu__label">Sort</span>
                  <div class="tasks-filter-menu__options">
                    @for (option of sortOptions; track option.value) {
                      <button
                        type="button"
                        class="tasks-filter-menu__chip"
                        [class.tasks-filter-menu__chip--active]="selectedSort() === option.value"
                        (click)="onSortChange(option.value)"
                      >
                        {{ option.label }}
                      </button>
                    }
                  </div>
                </div>
                <div class="tasks-filter-menu__section">
                  <span class="tasks-filter-menu__label">Tags</span>
                  <mat-form-field appearance="outline" style="width: 100%;">
                    <mat-label>Filter by tags</mat-label>
                    <mat-select
                      multiple
                      [value]="selectedTagFilters()"
                      (selectionChange)="onTagFilterChange($event.value)"
                    >
                      @for (tag of filterTagOptions(); track tag) {
                        <mat-option [value]="tag">{{ tag }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                <div class="tasks-filter-menu__section">
                  <span class="tasks-filter-menu__label">Assignees</span>
                  <mat-form-field appearance="outline" style="width: 100%;">
                    <mat-label>Filter by assignees</mat-label>
                    <mat-select
                      multiple
                      [value]="selectedAssigneeFilters()"
                      (selectionChange)="onAssigneeFilterChange($event.value)"
                    >
                      @for (user of filterAssigneeOptions(); track user.id) {
                        <mat-option [value]="user.id">{{ user.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                <div class="tasks-filter-menu__section">
                  <div class="tasks-filter-menu__saved-header">
                    <span class="tasks-filter-menu__label">Saved views</span>
                    <button
                      type="button"
                      class="tasks-filter-menu__save"
                      [disabled]="!canSaveCurrentFilter()"
                      (click)="closeMenu(tasksFilterTrigger); openSaveFilterDialog()"
                    >
                      Save current
                    </button>
                  </div>

                  @if (visibleSavedFilters().length) {
                    <div class="tasks-filter-menu__saved-list">
                      @for (filter of visibleSavedFilters(); track filter.id) {
                        <div class="tasks-filter-menu__saved-item" [class.tasks-filter-menu__saved-item--active]="isSavedFilterActive(filter)">
                          <button
                            type="button"
                            class="tasks-filter-menu__saved-apply"
                            (click)="applySavedFilter(filter)"
                          >
                            <strong>{{ filter.name }}</strong>
                            <span>{{ getScopeLabel(filter.scope) }} · {{ getSortLabel(filter.sort) }} · {{ formatSavedFilterContext(filter) }}</span>
                            @if (filter.searchTerm) {
                              <small>Search: {{ filter.searchTerm }}</small>
                            }
                            @if (filter.selectedTags.length) {
                              <small>Tags: {{ filter.selectedTags.join(', ') }}</small>
                            }
                            @if (filter.selectedAssigneeIds.length) {
                              <small>Assignees: {{ formatAssigneeFilterNames(filter.selectedAssigneeIds) }}</small>
                            }
                          </button>
                          <div class="tasks-filter-menu__saved-actions">
                            <button
                              type="button"
                              class="tasks-filter-menu__saved-toggle"
                              [class.tasks-filter-menu__saved-toggle--active]="filter.isFavorite"
                              aria-label="Toggle favorite view"
                              (click)="toggleSavedFilterFavorite(filter)"
                            >
                              <mat-icon aria-hidden="true">{{ filter.isFavorite ? 'star' : 'star_border' }}</mat-icon>
                            </button>
                            <button
                              type="button"
                              class="tasks-filter-menu__saved-toggle"
                              [class.tasks-filter-menu__saved-toggle--active]="isDefaultSavedFilter(filter)"
                              aria-label="Set default view"
                              (click)="setDefaultSavedFilter(filter)"
                            >
                              <mat-icon aria-hidden="true">push_pin</mat-icon>
                            </button>
                          </div>
                          <button
                            type="button"
                            class="tasks-filter-menu__saved-delete"
                            aria-label="Delete saved view"
                            (click)="deleteSavedFilter(filter)"
                          >
                            <mat-icon aria-hidden="true">delete</mat-icon>
                          </button>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="tasks-filter-menu__empty">Save a filtered view to reuse it quickly.</p>
                  }
                </div>
              </div>
            </mat-menu>
          }
        </div>

        <section class="tasks-toolbar"></section>
      </header>

      @if (selectedProjectId() && selectedView() !== 'reports') {
        <section class="tasks-dashboard" aria-label="Task insights">
          @for (insight of dashboardInsights(); track insight.label) {
            <article class="tasks-dashboard__card" [attr.data-tone]="insight.tone">
              <span>{{ insight.label }}</span>
              <strong>{{ insight.value }}</strong>
            </article>
          }
        </section>
      } @else if (selectedProjectId() && selectedView() === 'reports' && canManageProject()) {
        <section class="tasks-dashboard tasks-dashboard--reports" aria-label="Task reporting summary">
          @for (insight of activeReportSummaryCards(); track insight.label) {
            <article class="tasks-dashboard__card" [attr.data-tone]="insight.tone">
              <span>{{ insight.label }}</span>
              <strong>{{ insight.value }}</strong>
              <small>{{ insight.hint }}</small>
            </article>
          }
        </section>
      }

      @if (hasBulkSelection()) {
        <section class="tasks-bulk-bar" aria-label="Bulk task actions">
          <strong>{{ selectedTasks().length }} selected</strong>
          <div class="tasks-bulk-bar__actions">
            <mat-form-field appearance="outline" class="tasks-bulk-bar__field">
              <mat-label>Move to</mat-label>
              <mat-select (selectionChange)="bulkMoveTasks($event.value)">
                @for (status of statuses(); track status.key) {
                  <mat-option [value]="status.key">{{ status.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <button type="button" mat-stroked-button color="warn" (click)="bulkArchiveTasks()">Archive</button>
            <button type="button" mat-button (click)="clearTaskSelection()">Clear</button>
          </div>
        </section>
      }

      @if (!projects().length) {
        <mat-card class="tasks-empty-card">
          <mat-icon aria-hidden="true">assignment</mat-icon>
          <h3>No projects yet</h3>
          <p>Create your first project to start organizing work in {{ brand.name }}.</p>
          @if (canManageProject()) {
            <button type="button" mat-flat-button color="primary" (click)="openProjectDialog()">Create project</button>
          }
        </mat-card>
      } @else if (!selectedProjectId()) {
        <mat-card class="tasks-empty-card">
          <mat-icon aria-hidden="true">folder</mat-icon>
          <h3>Select a project</h3>
          <p>Choose a project from the header switcher to view its tasks.</p>
        </mat-card>
      } @else if (selectedView() === 'reports') {
        <section class="tasks-reports" [class.tasks-reports--admin]="canManageProject()" aria-label="Task reports">
          <section class="tasks-reports__hero">
            <div class="tasks-reports__hero-copy">
              <p class="tasks-reports__eyebrow">{{ canManageProject() ? 'Admin reports' : 'Personal reports' }}</p>
              <h3>{{ reportAudienceTitle() }}</h3>
              <p>{{ reportAudienceDescription() }}</p>
            </div>
            <mat-tab-group class="tasks-reports__tabs" [selectedIndex]="reportTabIndex()" (selectedTabChange)="onReportTabChange($event.index)">
              @for (tab of reportTabs(); track tab.value) {
                <mat-tab [label]="tab.label">
                </mat-tab>
              }
            </mat-tab-group>
          </section>
          <div class="tasks-reports__body">
            @if (activeReportTab() === 'overview') {
              <section class="tasks-report-overview">
                <div class="tasks-report-overview__highlights">
                  @for (item of overviewHighlights(); track item.label) {
                    <article class="tasks-report-overview__highlight" [attr.data-tone]="item.tone">
                      <span>{{ item.label }}</span>
                      <strong>{{ item.value }}</strong>
                      <small>{{ item.hint }}</small>
                    </article>
                  }
                </div>

                <div class="tasks-report-overview__split">
                  @if (canManageProject()) {
                    <article class="tasks-report-card tasks-report-overview__panel">
                      <header class="tasks-report-card__header">
                        <div>
                          <strong>Risk watchlist</strong>
                          <span>Overdue, urgent, or unassigned tasks that need attention first.</span>
                        </div>
                      </header>
                      <div class="tasks-report-card__task-list">
                        @for (task of adminOverviewRiskTasks(); track task.id) {
                          <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetails(task)">
                            <span>{{ task.title }}</span>
                            <small>{{ getDueBadgeLabel(task.due_at) }} · {{ task.priority }} · {{ getTaskAssigneeSummary(task) }}</small>
                          </button>
                        } @empty {
                          <p class="tasks-report-card__empty">No priority risks right now.</p>
                        }
                      </div>
                    </article>

                    <article class="tasks-report-card tasks-report-overview__panel">
                      <header class="tasks-report-card__header">
                        <div>
                          <strong>Workload hotspots</strong>
                          <span>Who is carrying the heaviest open load across the project.</span>
                        </div>
                      </header>
                      <div class="tasks-report-overview__person-list">
                        @for (row of assigneeWorkload(); track row.id) {
                          <div class="tasks-report-overview__person-row">
                            <div class="tasks-report-overview__person-meta">
                              <span class="task-avatar task-avatar--small">{{ getUserInitials(row.id || null) }}</span>
                              <div>
                                <strong>{{ row.name }}</strong>
                                <span>{{ row.total }} open · {{ row.dueToday }} due today</span>
                              </div>
                            </div>
                            <small [class.tasks-report-overview__metric--alert]="row.overdue > 0">{{ row.overdue }} overdue</small>
                          </div>
                        } @empty {
                          <p class="tasks-report-card__empty">No assigned workload yet.</p>
                        }
                        @if (unassignedWorkload().total > 0) {
                          <div class="tasks-report-overview__person-row tasks-report-overview__person-row--unassigned">
                            <div class="tasks-report-overview__person-meta">
                              <span class="task-avatar task-avatar--small">UA</span>
                              <div>
                                <strong>Unassigned work</strong>
                                <span>{{ unassignedWorkload().total }} open · {{ unassignedWorkload().dueToday }} due today</span>
                              </div>
                            </div>
                            <small [class.tasks-report-overview__metric--alert]="unassignedWorkload().overdue > 0">{{ unassignedWorkload().overdue }} overdue</small>
                          </div>
                        }
                      </div>
                    </article>
                  } @else {
                    <article class="tasks-report-card tasks-report-overview__panel">
                      <header class="tasks-report-card__header">
                        <div>
                          <strong>Needs attention</strong>
                          <span>Your overdue and upcoming assigned work.</span>
                        </div>
                      </header>
                      <div class="tasks-report-card__task-list">
                        @for (task of personalOverviewAttentionTasks(); track task.id) {
                          <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetails(task)">
                            <span>{{ task.title }}</span>
                            <small>{{ getDueBadgeLabel(task.due_at) }} · {{ formatStatus(task.status) }}</small>
                          </button>
                        } @empty {
                          <p class="tasks-report-card__empty">No urgent items on your plate.</p>
                        }
                      </div>
                    </article>

                    <article class="tasks-report-card tasks-report-overview__panel">
                      <header class="tasks-report-card__header">
                        <div>
                          <strong>My work queue</strong>
                          <span>Your most recently active assigned tasks.</span>
                        </div>
                      </header>
                      <div class="tasks-report-card__task-list">
                        @for (task of personalOverviewCurrentTasks(); track task.id) {
                          <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetails(task)">
                            <span>{{ task.title }}</span>
                            <small>{{ formatStatus(task.status) }} · {{ getDueBadgeLabel(task.due_at) }}</small>
                          </button>
                        } @empty {
                          <p class="tasks-report-card__empty">No assigned work yet.</p>
                        }
                      </div>
                    </article>
                  }
                </div>
              </section>

              @if (canManageProject()) {
                <section class="tasks-report-section">
                  <div class="tasks-report-section__header">
                    <div>
                      <h3>Current work</h3>
                      <p>See who is actively working on subtasks right now.</p>
                    </div>
                    <div class="tasks-report-section__actions">
                      <button type="button" mat-stroked-button (click)="copyCurrentWorkReport()">Copy brief</button>
                      <button type="button" mat-stroked-button (click)="copyCurrentWorkDetailed()">Copy detailed</button>
                      <button type="button" mat-stroked-button (click)="exportCurrentWorkCsv()">CSV</button>
                      <button type="button" mat-stroked-button (click)="printReportSummaryView()">Print summary</button>
                    </div>
                  </div>
                  <div class="tasks-report-grid tasks-report-grid--current">
                    @for (bucket of currentWorkReport(); track bucket.id) {
                      <article class="tasks-report-card">
                        <header class="tasks-report-card__header">
                          <div>
                            <strong>{{ bucket.name }}</strong>
                            <span>{{ bucket.total }} active subtasks</span>
                          </div>
                          <span class="tasks-report-card__pill" [class.tasks-report-card__pill--alert]="bucket.overdue > 0">
                            {{ bucket.overdue }} overdue
                          </span>
                        </header>
                        <div class="tasks-report-card__metric-row">
                          <span>Tracked</span>
                          <strong>{{ formatMinutes(bucket.trackedMinutes) }}</strong>
                        </div>
                        <div class="tasks-report-card__task-list">
                          @for (task of bucket.tasks.slice(0, 4); track task.id) {
                            <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetails(task)">
                              <span>{{ task.title }}</span>
                              <small>{{ getDueBadgeLabel(task.due_at) }}</small>
                            </button>
                          } @empty {
                            <p class="tasks-report-card__empty">No active tasks.</p>
                          }
                        </div>
                      </article>
                    } @empty {
                      <mat-card class="tasks-empty-card tasks-empty-card--compact">
                        <mat-icon aria-hidden="true">work_history</mat-icon>
                        <h3>No active work</h3>
                        <p>No one is currently marked as in progress for this project.</p>
                      </mat-card>
                    }
                  </div>
                </section>

                <section class="tasks-report-section">
                  <div class="tasks-report-section__header">
                    <div>
                      <h3>Future planned tasks</h3>
                      <p>Backlog and to-do work organized by upcoming time horizon.</p>
                    </div>
                    <div class="tasks-report-section__actions">
                      <button type="button" mat-stroked-button (click)="copyPlannedTaskReport()">Copy brief</button>
                      <button type="button" mat-stroked-button (click)="copyPlannedTaskDetailed()">Copy detailed</button>
                      <button type="button" mat-stroked-button (click)="exportPlannedTasksCsv()">CSV</button>
                    </div>
                  </div>
                  <div class="tasks-report-grid tasks-report-grid--planned">
                    @for (bucket of plannedTaskBuckets(); track bucket.label) {
                      <article class="tasks-report-card">
                        <header class="tasks-report-card__header">
                          <div>
                            <strong>{{ bucket.label }}</strong>
                            <span>{{ bucket.hint }}</span>
                          </div>
                          <span class="tasks-report-card__pill">{{ bucket.tasks.length }}</span>
                        </header>
                        <div class="tasks-report-card__task-list">
                          @for (task of bucket.tasks.slice(0, 6); track task.id) {
                            <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetails(task)">
                              <span>{{ task.title }}</span>
                              <small>{{ getTaskAssigneeSummary(task) }}</small>
                            </button>
                          } @empty {
                            <p class="tasks-report-card__empty">Nothing in this bucket.</p>
                          }
                        </div>
                      </article>
                    }
                  </div>
                </section>
              }
            }

            @if (activeReportTab() === 'team') {
              <section class="tasks-report-section">
                <div class="tasks-report-section__header">
                  <div>
                    <h3>Utilization report</h3>
                    <p>Open load, tracked time, and estimated effort by subtask owner.</p>
                  </div>
                  <div class="tasks-report-section__actions">
                    <mat-form-field appearance="outline" class="tasks-report-section__date-field">
                      <mat-label>From</mat-label>
                      <input matInput type="date" [value]="utilizationRangeStart()" (change)="utilizationRangeStart.set($any($event.target).value)" />
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="tasks-report-section__date-field">
                      <mat-label>To</mat-label>
                      <input matInput type="date" [value]="utilizationRangeEnd()" (change)="utilizationRangeEnd.set($any($event.target).value)" />
                    </mat-form-field>
                  </div>
                </div>
                <div class="tasks-utilization">
                  @for (row of utilizationReport(); track row.id) {
                    <article class="tasks-utilization__row">
                      <div class="tasks-utilization__person">
                        <span class="task-avatar">{{ getUserInitials(row.id || null) }}</span>
                        <div>
                          <strong>{{ row.name }}</strong>
                          <span>{{ row.total }} open · {{ row.inProgress }} active · {{ row.overdue }} overdue</span>
                        </div>
                      </div>
                      <div class="tasks-utilization__metrics">
                        <span>Est {{ formatMinutes(row.estimatedMinutes) }}</span>
                        <span>Tracked {{ formatMinutes(row.trackedMinutes) }}</span>
                        <span>{{ row.utilization === null ? 'No estimate' : row.utilization + '%' }}</span>
                      </div>
                    </article>
                  }
                </div>
              </section>

              <section class="tasks-report-section">
                <div class="tasks-report-section__header">
                  <div>
                    <h3>User timesheets</h3>
                    <p>Logged time entries by user across the selected date range.</p>
                  </div>
                </div>
                <div class="tasks-utilization">
                  @for (row of userTimesheets(); track row.id) {
                    <article class="tasks-utilization__row">
                      <div class="tasks-utilization__person">
                        <span class="task-avatar">{{ getUserInitials(row.id || null) }}</span>
                        <div>
                          <strong>{{ row.name }}</strong>
                          <span>{{ row.entryCount }} entries · {{ row.runningCount }} running</span>
                        </div>
                      </div>
                      <div class="tasks-utilization__metrics">
                        <span>Total {{ formatMinutes(row.totalMinutes) }}</span>
                        <span>Latest {{ row.latestEntryAt ? formatDueDate(row.latestEntryAt) : 'None' }}</span>
                      </div>
                    </article>
                  } @empty {
                    <mat-card class="tasks-empty-card tasks-empty-card--compact">
                      <mat-icon aria-hidden="true">schedule</mat-icon>
                      <h3>No timesheets yet</h3>
                      <p>No time has been logged in the selected range.</p>
                    </mat-card>
                  }
                </div>
              </section>
            }

            @if (activeReportTab() === 'planning') {
              <section class="tasks-report-section">
                <div class="tasks-report-section__header">
                  <div>
                    <h3>Sprint planning</h3>
                    <p>Suggested sprint groupings based on priority and near-term dates.</p>
                  </div>
                  <div class="tasks-report-section__actions">
                    <button type="button" mat-stroked-button (click)="copySprintPlanReport()">Copy brief</button>
                    <button type="button" mat-stroked-button (click)="copySprintPlanDetailed()">Copy detailed</button>
                    <button type="button" mat-stroked-button (click)="exportSprintPlanCsv()">CSV</button>
                    @if (canManageProject()) {
                      <button type="button" mat-stroked-button (click)="openSprintDialog()">New sprint</button>
                    }
                  </div>
                </div>
                <div class="tasks-report-grid tasks-report-grid--planned">
                  @for (sprint of sprintPlans(); track sprint.label) {
                    <article class="tasks-report-card">
                      <header class="tasks-report-card__header">
                        <div>
                          <strong>{{ sprint.label }}</strong>
                          <span>{{ sprint.hint }}</span>
                        </div>
                        <div class="tasks-report-card__header-actions">
                          <span class="tasks-report-card__pill">{{ sprint.tasks.length }}</span>
                          @if (canManageProject() && getSprintByName(sprint.label); as editableSprint) {
                            <button type="button" mat-icon-button class="tasks-report-card__edit tasks-surface-icon-button" (click)="openSprintDialog(editableSprint)" aria-label="Edit sprint">
                              <mat-icon aria-hidden="true">edit</mat-icon>
                            </button>
                          }
                        </div>
                      </header>
                      <div class="tasks-report-card__task-list">
                        @for (task of sprint.tasks; track task.id) {
                          <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetails(task)">
                            <span>{{ task.title }}</span>
                            <small>{{ formatStatus(task.status) }} · {{ task.priority }}</small>
                          </button>
                        } @empty {
                          <p class="tasks-report-card__empty">No sprint candidates yet.</p>
                        }
                      </div>
                    </article>
                  }
                </div>
              </section>
            }

            @if (activeReportTab() === 'time') {
              <section class="tasks-report-section">
                <div class="tasks-report-section__header">
                  <div>
                    <h3>Subtask time report</h3>
                    <p>See how much time each person has spent on every subtask in the selected date range.</p>
                  </div>
                </div>
                <div class="tasks-report-grid tasks-report-grid--planned">
                  @for (row of subtaskTimeReport(); track row.subtaskId) {
                    <article class="tasks-report-card">
                      <header class="tasks-report-card__header">
                        <div>
                          <strong>{{ row.subtaskTitle }}</strong>
                          <span>{{ row.taskTitle }}</span>
                        </div>
                        <span class="tasks-report-card__pill">{{ formatMinutes(row.totalMinutes) }}</span>
                      </header>
                      <div class="tasks-report-card__metric-stack">
                        <span>Owner: {{ row.assigneeName }}</span>
                        <span>{{ row.contributors.length }} contributor{{ row.contributors.length === 1 ? '' : 's' }}</span>
                      </div>
                      <div class="tasks-report-card__task-list">
                        @for (contributor of row.contributors; track contributor.userId) {
                          <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetailsById(row.taskId)">
                            <span>{{ contributor.name }}</span>
                            <small>{{ formatMinutes(contributor.minutes) }}</small>
                          </button>
                        }
                      </div>
                    </article>
                  } @empty {
                    <mat-card class="tasks-empty-card tasks-empty-card--compact">
                      <mat-icon aria-hidden="true">timer</mat-icon>
                      <h3>No subtask time yet</h3>
                      <p>Once people log time against subtasks, the breakdown will appear here.</p>
                    </mat-card>
                  }
                </div>

                <section class="tasks-report-section">
                  <div class="tasks-report-section__header">
                    <div>
                      <h3>Utilization over time</h3>
                      <p>Daily tracked minutes by user for the selected range.</p>
                    </div>
                  </div>
                  <div class="tasks-timeline">
                    @for (row of utilizationTimeline(); track row.id) {
                      <article class="tasks-timeline__row">
                        <div class="tasks-timeline__person">
                          <span class="task-avatar">{{ getUserInitials(row.id || null) }}</span>
                          <strong>{{ row.name }}</strong>
                        </div>
                        <div class="tasks-timeline__bars">
                          @for (day of row.dailyMinutes; track day.label) {
                            <div class="tasks-timeline__bar" [style.flex]="getTimelineFlex(day.minutes)">
                              <span>{{ day.label.slice(5) }}</span>
                              <strong>{{ formatMinutes(day.minutes) }}</strong>
                            </div>
                          }
                        </div>
                      </article>
                    } @empty {
                      <mat-card class="tasks-empty-card tasks-empty-card--compact">
                        <mat-icon aria-hidden="true">bar_chart</mat-icon>
                        <h3>No utilization data</h3>
                        <p>Log time on tasks to see utilization trends here.</p>
                      </mat-card>
                    }
                  </div>
                </section>
              </section>
            }

            @if (activeReportTab() === 'tags') {
              <section class="tasks-report-section">
                <div class="tasks-report-section__header">
                  <div>
                    <h3>Reports by tag</h3>
                    <p>Filter reporting views by tags and inspect workload distribution.</p>
                  </div>
                  @if (selectedReportTag()) {
                    <div class="tasks-report-section__actions">
                      <button type="button" mat-stroked-button (click)="copyTaggedReport()">Copy brief</button>
                      <button type="button" mat-stroked-button (click)="copyTaggedReportDetailed()">Copy detailed</button>
                      <button type="button" mat-stroked-button (click)="exportTaggedReportCsv()">CSV</button>
                    </div>
                  }
                </div>
                <div class="tasks-report-tags">
                  @for (tag of reportTagSummary(); track tag.tag) {
                    <button
                      type="button"
                      class="tasks-report-tags__chip"
                      [class.tasks-report-tags__chip--active]="selectedReportTag() === tag.tag"
                      (click)="toggleReportTag(tag.tag)"
                    >
                      <span>{{ tag.tag }}</span>
                      <small>{{ tag.count }}</small>
                    </button>
                  } @empty {
                    <p class="tasks-report-card__empty">No tags are in use yet.</p>
                  }
                </div>

                @if (selectedReportTag()) {
                  <div class="tasks-report-tag-summary">
                    <article class="tasks-report-card">
                      <header class="tasks-report-card__header">
                        <div>
                          <strong>{{ selectedReportTag() }}</strong>
                          <span>Current tag report</span>
                        </div>
                        <span class="tasks-report-card__pill">{{ getTagTaskCount(selectedReportTag()!) }} tasks</span>
                      </header>
                      <div class="tasks-report-card__metric-stack">
                        <span>{{ getTagInProgressCount(selectedReportTag()!) }} in progress</span>
                        <span>{{ getTagOverdueCount(selectedReportTag()!) }} overdue</span>
                      </div>
                      <div class="tasks-report-card__task-list">
                        @for (task of taggedReportTasks().slice(0, 8); track task.id) {
                          <button type="button" class="tasks-report-card__task-link" (click)="openTaskDetails(task)">
                            <span>{{ task.title }}</span>
                            <small>{{ formatStatus(task.status) }} · {{ getTaskAssigneeSummary(task) }}</small>
                          </button>
                        } @empty {
                          <p class="tasks-report-card__empty">No tasks for this tag.</p>
                        }
                      </div>
                    </article>
                  </div>
                }
              </section>
            }
          </div>
        </section>
      } @else if (selectedView() === 'board') {
        <section class="tasks-board" aria-label="Task board" cdkDropList cdkDropListOrientation="horizontal" [cdkDropListData]="statuses()" (cdkDropListDropped)="onColumnDrop($event)">
          @for (status of statuses(); track status.key) {
            <article class="tasks-column" [attr.data-status]="status.key" cdkDrag [cdkDragDisabled]="!canManageProject()">
              <header class="tasks-column__header">
                <div class="tasks-column__title-block">
                  <span class="tasks-column__dot" aria-hidden="true"></span>
                  <h3>{{ status.label }}</h3>
                  <span class="tasks-column__count">{{ projectTasks(status.key).length }}</span>
                </div>
                <div class="tasks-column__actions">
                  <button type="button" mat-icon-button class="tasks-column__menu-button" [matMenuTriggerFor]="columnMenu" #columnMenuTrigger="matMenuTrigger" aria-label="Column actions">
                    <mat-icon aria-hidden="true">more_vert</mat-icon>
                  </button>
                  <mat-menu #columnMenu="matMenu">
                    <button type="button" mat-menu-item [disabled]="!selectedProjectId()" (click)="closeMenu(columnMenuTrigger); openTaskDialog(undefined, status.key)">
                      <mat-icon aria-hidden="true">add</mat-icon>
                      <span>Add task</span>
                    </button>
                    @if (canManageColumns()) {
                      <button type="button" mat-menu-item (click)="closeMenu(columnMenuTrigger); openRenameColumnDialog(status)">
                        <mat-icon aria-hidden="true">edit</mat-icon>
                        <span>Rename column</span>
                      </button>
                      <button type="button" mat-menu-item (click)="closeMenu(columnMenuTrigger); deleteColumn(status)">
                        <mat-icon aria-hidden="true">delete</mat-icon>
                        <span>Delete column</span>
                      </button>
                    }
                  </mat-menu>
                </div>
              </header>

              <div
                class="tasks-column__body"
                cdkDropList
                [id]="dropListId(status.key)"
                [cdkDropListData]="projectTasks(status.key)"
                [cdkDropListConnectedTo]="boardDropListIds()"
                (cdkDropListDropped)="onTaskDrop($event, status.key)"
              >
                @for (task of projectTasks(status.key); track task.id) {
                  <mat-card
                    class="task-card"
                    cdkDrag
                    [cdkDragData]="task"
                    [cdkDragDisabled]="!canEditTask(task)"
                  >
                    <div class="task-card__top">
                      <div class="task-card__title-block">
                        <div class="task-card__control-row">
                          @if (canEditTask(task)) {
                            <mat-checkbox
                              class="task-card__select"
                              [checked]="isTaskSelected(task.id)"
                              (click)="$event.stopPropagation()"
                              (change)="toggleTaskSelection(task.id, $event.checked)"
                            ></mat-checkbox>
                          }
                          @if (task.task_code) {
                            <span class="task-priority">{{ task.task_code.toUpperCase() }}</span>
                          }
                          <span class="task-priority" [attr.data-priority]="task.priority">{{ task.priority }}</span>
                        </div>
                        <div class="task-card__title-row">
                          <button
                            type="button"
                            class="task-card__title-link"
                            (click)="$event.stopPropagation(); openTaskDetails(task)"
                          >
                            {{ task.title }}
                          </button>
                        </div>
                      </div>
                      <div class="task-card__top-actions">
                        @if (canEditTask(task)) {
                          <button
                            type="button"
                            mat-icon-button
                            class="task-card__menu-button tasks-surface-icon-button"
                            [matMenuTriggerFor]="taskCardMenu"
                            #taskCardMenuTrigger="matMenuTrigger"
                            aria-label="Task actions"
                            (click)="$event.stopPropagation()"
                          >
                            <mat-icon aria-hidden="true">more_vert</mat-icon>
                          </button>
                          <mat-menu #taskCardMenu="matMenu">
                            <button type="button" mat-menu-item (click)="$event.stopPropagation(); closeMenu(taskCardMenuTrigger); openTaskDialog(task)">
                              <mat-icon aria-hidden="true">edit</mat-icon>
                              <span>Edit task</span>
                            </button>
                            <button type="button" mat-menu-item (click)="$event.stopPropagation(); closeMenu(taskCardMenuTrigger); archiveTask(task)">
                              <mat-icon aria-hidden="true">archive</mat-icon>
                              <span>Archive task</span>
                            </button>
                          </mat-menu>
                        }
                      </div>
                    </div>

                    <div class="task-card__meta">
                      @if (getTaskAssigneeIds(task).length > 1) {
                        <span class="task-card__assignee task-card__assignee--avatars">
                          <span class="task-assignee-stack" aria-label="Task subtask owners">
                            @for (assigneeId of getVisibleTaskAssigneeIds(task); track assigneeId) {
                              <app-user-hover-card
                                [name]="getUserName(assigneeId)"
                                [email]="getUserEmail(assigneeId)"
                                [avatarUrl]="getUserAvatarUrl(assigneeId)"
                                [initials]="getUserInitials(assigneeId)"
                                [quickMessageUserId]="assigneeId"
                                [canQuickMessage]="!!assigneeId && assigneeId !== currentUser()?.id"
                              >
                                <span class="task-avatar task-avatar--small task-avatar--stacked">
                                  @if (getUserAvatarUrl(assigneeId); as avatarUrl) {
                                    <img [src]="avatarUrl" [alt]="getUserName(assigneeId)" />
                                  } @else {
                                    {{ getUserInitials(assigneeId) }}
                                  }
                                </span>
                              </app-user-hover-card>
                            }
                            @if (getTaskAssigneeOverflowCount(task) > 0) {
                              <span class="task-avatar task-avatar--small task-avatar--stacked task-avatar--overflow">+{{ getTaskAssigneeOverflowCount(task) }}</span>
                            }
                          </span>
                        </span>
                      } @else {
                        <span class="task-card__assignee">
                          <app-user-hover-card
                            [name]="getUserName(getPrimaryTaskAssigneeId(task))"
                            [email]="getUserEmail(getPrimaryTaskAssigneeId(task))"
                            [avatarUrl]="getUserAvatarUrl(getPrimaryTaskAssigneeId(task))"
                            [initials]="getUserInitials(getPrimaryTaskAssigneeId(task))"
                            [quickMessageUserId]="getPrimaryTaskAssigneeId(task)"
                            [canQuickMessage]="!!getPrimaryTaskAssigneeId(task) && getPrimaryTaskAssigneeId(task) !== currentUser()?.id"
                          >
                            <span class="task-avatar task-avatar--small task-avatar--stacked">
                              @if (getUserAvatarUrl(getPrimaryTaskAssigneeId(task)); as avatarUrl) {
                                <img [src]="avatarUrl" [alt]="getUserName(getPrimaryTaskAssigneeId(task))" />
                              } @else {
                                {{ getUserInitials(getPrimaryTaskAssigneeId(task)) }}
                              }
                            </span>
                          </app-user-hover-card>
                          {{ getTaskAssigneeSummary(task) }}
                        </span>
                      }
                    </div>

                    <div class="task-card__schedule">
                      <span class="task-card__due" [attr.data-state]="getDueState(task.due_at)">
                        <mat-icon aria-hidden="true">event</mat-icon>{{ getDueBadgeLabel(task.due_at) }}
                      </span>
                    </div>

                    @if (task.sprint_id) {
                      <div class="task-card__sprint">
                        <span class="task-sprint-chip">{{ getSprintName(task.sprint_id) }}</span>
                        @if (hasRunningTimer(task)) {
                          <span class="task-sprint-chip task-sprint-chip--running">Running</span>
                        }
                      </div>
                    } @else if (hasRunningTimer(task)) {
                      <div class="task-card__sprint">
                        <span class="task-sprint-chip task-sprint-chip--running">Running</span>
                      </div>
                    }

                    @if (task.tags?.length) {
                      <div class="task-tags">
                        @for (tag of task.tags; track tag) {
                          <span>{{ tag }}</span>
                        }
                      </div>
                    }

                    @if ((task.subtasks?.length ?? 0) || (task.comments?.length ?? 0) || (task.attachments?.length ?? 0) || (task.activity?.length ?? 0) || task.estimated_minutes || task.tracked_minutes) {
                      <div class="task-card__divider" aria-hidden="true"></div>
                    }

                    <div class="task-card__signals">
                      <span class="task-card__signal">
                        <mat-icon aria-hidden="true">checklist</mat-icon>{{ getCompletedSubtaskCount(task) }}/{{ task.subtasks?.length ?? 0 }}
                      </span>
                      <span class="task-card__signal">
                        <mat-icon aria-hidden="true">comment</mat-icon>{{ task.comments?.length ?? 0 }}
                      </span>
                      <span class="task-card__signal">
                        <mat-icon aria-hidden="true">attach_file</mat-icon>{{ task.attachments?.length ?? 0 }}
                      </span>
                      <span class="task-card__signal">
                        <mat-icon aria-hidden="true">history</mat-icon>{{ task.activity?.length ?? 0 }}
                      </span>
                      <span class="task-card__signal">
                        <mat-icon aria-hidden="true">schedule</mat-icon>{{ formatMinutes(task.estimated_minutes) }}
                      </span>
                      <span class="task-card__signal">
                        <mat-icon aria-hidden="true">timer</mat-icon>{{ formatMinutes(task.tracked_minutes) }}
                      </span>
                    </div>
                  </mat-card>
                } @empty {
                  <div class="tasks-column__empty">No tasks</div>
                }
              </div>
            </article>
          }
        </section>
      } @else {
        <section class="tasks-list" aria-label="Task list">
          <div class="tasks-list__header" aria-hidden="true">
            <span>Select</span>
            <span>Task</span>
            <span>Subtask owners</span>
            <span>Schedule</span>
            <span>Priority</span>
            <span></span>
          </div>
          @for (task of projectTasks(); track task.id) {
            <mat-card class="task-row" (click)="openTaskDetails(task)">
              <div class="task-row__select">
                @if (canEditTask(task)) {
                  <mat-checkbox
                    [checked]="isTaskSelected(task.id)"
                    (click)="$event.stopPropagation()"
                    (change)="toggleTaskSelection(task.id, $event.checked)"
                  ></mat-checkbox>
                }
              </div>
              <div class="task-row__main">
                <div class="task-row__title">
                  <button type="button" class="task-row__title-link" (click)="$event.stopPropagation(); openTaskDetails(task)">
                    @if (task.task_code) {
                      <small>{{ task.task_code.toUpperCase() }}</small>
                    }
                    {{ task.title }}
                  </button>
                  <span class="task-status" [attr.data-status]="task.status">{{ formatStatus(task.status) }}</span>
                </div>
                @if (summarizeDescription(task.description)) {
                  <p>{{ summarizeDescription(task.description) }}</p>
                }
                @if (task.tags?.length || task.sprint_id || hasRunningTimer(task)) {
                  <div class="task-row__chips">
                    @if (task.sprint_id) {
                      <span class="task-sprint-chip task-sprint-chip--row">{{ getSprintName(task.sprint_id) }}</span>
                    }
                    @if (hasRunningTimer(task)) {
                      <span class="task-sprint-chip task-sprint-chip--row task-sprint-chip--running">Running</span>
                    }
                    @for (tag of task.tags ?? []; track tag) {
                      <span class="task-row__tag">{{ tag }}</span>
                    }
                  </div>
                }
              </div>

              <div class="task-row__assignee-cell">
                @if (getTaskAssigneeIds(task).length > 1) {
                  <span class="task-row__assignee task-row__assignee--avatars">
                    <span class="task-assignee-stack task-assignee-stack--small" aria-label="Task subtask owners">
                      @for (assigneeId of getVisibleTaskAssigneeIds(task); track assigneeId) {
                        <app-user-hover-card
                          [name]="getUserName(assigneeId)"
                          [email]="getUserEmail(assigneeId)"
                          [avatarUrl]="getUserAvatarUrl(assigneeId)"
                          [initials]="getUserInitials(assigneeId)"
                          [quickMessageUserId]="assigneeId"
                          [canQuickMessage]="!!assigneeId && assigneeId !== currentUser()?.id"
                        >
                          <span class="task-avatar task-avatar--small task-avatar--stacked">
                            @if (getUserAvatarUrl(assigneeId); as avatarUrl) {
                              <img [src]="avatarUrl" [alt]="getUserName(assigneeId)" />
                            } @else {
                              {{ getUserInitials(assigneeId) }}
                            }
                          </span>
                        </app-user-hover-card>
                      }
                      @if (getTaskAssigneeOverflowCount(task) > 0) {
                        <span class="task-avatar task-avatar--small task-avatar--stacked task-avatar--overflow">+{{ getTaskAssigneeOverflowCount(task) }}</span>
                      }
                    </span>
                  </span>
                } @else {
                  <span class="task-row__assignee">
                    <app-user-hover-card
                      [name]="getUserName(getPrimaryTaskAssigneeId(task))"
                      [email]="getUserEmail(getPrimaryTaskAssigneeId(task))"
                      [avatarUrl]="getUserAvatarUrl(getPrimaryTaskAssigneeId(task))"
                      [initials]="getUserInitials(getPrimaryTaskAssigneeId(task))"
                      [quickMessageUserId]="getPrimaryTaskAssigneeId(task)"
                      [canQuickMessage]="!!getPrimaryTaskAssigneeId(task) && getPrimaryTaskAssigneeId(task) !== currentUser()?.id"
                    >
                      <span class="task-avatar task-avatar--small task-avatar--stacked">
                        @if (getUserAvatarUrl(getPrimaryTaskAssigneeId(task)); as avatarUrl) {
                          <img [src]="avatarUrl" [alt]="getUserName(getPrimaryTaskAssigneeId(task))" />
                        } @else {
                          {{ getUserInitials(getPrimaryTaskAssigneeId(task)) }}
                        }
                      </span>
                    </app-user-hover-card>
                    {{ getTaskAssigneeSummary(task) }}
                  </span>
                }
              </div>

              <div class="task-row__schedule">
                @if (task.start_at) {
                  <span class="task-row__due" data-state="upcoming">Starts {{ formatDateOnly(task.start_at) }}</span>
                }
                <span class="task-row__due" [attr.data-state]="getDueState(task.due_at)">{{ getDueBadgeLabel(task.due_at) }}</span>
                <span class="task-row__metric">Est {{ formatMinutes(task.estimated_minutes) }}</span>
                <span class="task-row__metric">Tracked {{ formatMinutes(task.tracked_minutes) }}</span>
              </div>

              <div class="task-row__priority-cell">
                <span class="task-priority" [attr.data-priority]="task.priority">{{ task.priority }}</span>
              </div>

              <div class="task-row__actions">
                @if (canEditTask(task)) {
                  <button type="button" mat-icon-button class="tasks-surface-icon-button" [matMenuTriggerFor]="taskRowMenu" #taskRowMenuTrigger="matMenuTrigger" (click)="$event.stopPropagation()" aria-label="Task row actions">
                    <mat-icon aria-hidden="true">more_horiz</mat-icon>
                  </button>
                  <mat-menu #taskRowMenu="matMenu">
                    <button type="button" mat-menu-item (click)="$event.stopPropagation(); closeMenu(taskRowMenuTrigger); openTaskDialog(task)">
                      <mat-icon aria-hidden="true">edit</mat-icon>
                      <span>Edit task</span>
                    </button>
                    <button type="button" mat-menu-item (click)="$event.stopPropagation(); closeMenu(taskRowMenuTrigger); archiveTask(task)">
                      <mat-icon aria-hidden="true">archive</mat-icon>
                      <span>Archive task</span>
                    </button>
                  </mat-menu>
                }
              </div>
            </mat-card>
          } @empty {
            <mat-card class="tasks-empty-card tasks-empty-card--compact">
              <mat-icon aria-hidden="true">task_alt</mat-icon>
              <h3>No tasks yet</h3>
              <p>Create the first task for this project.</p>
            </mat-card>
          }
        </section>
      }
      @if (selectedTaskId()) {
        <button type="button" class="tasks-details-backdrop" aria-label="Close task details" (click)="closeTaskDetails()"></button>
        <aside class="tasks-details-shell">
          <app-task-details-panel
            [taskId]="selectedTaskId()!"
            [task]="selectedTask()"
            [organizationUsers]="getProjectUsers()"
            [statuses]="statuses()"
            [priorities]="priorities"
            [canEdit]="selectedTask() ? canEditTask(selectedTask()!) : false"
            (closed)="closeTaskDetails()"
            (taskUpdated)="handleTaskUpdated($event)"
          ></app-task-details-panel>
        </aside>
      }
    </section>
  </div>
</section>
